import { useContext, useEffect, useState } from "react"
import { Client } from "@xmpp/client"
import { useInView } from "react-intersection-observer"
import PQueue from "p-queue"
import { XmppContext } from "@/stream/xmppClient"
import { Message } from "@/reducers/messagesSlice"
import { SearchHit } from "@/lib/searchResultParser"
import { fetchSearchHistoryPage } from "@/api/searchHistory"
import { isSearchHistoryAnchor } from "@/lib/searchHistoryAnchor"

const queue = new PQueue({ concurrency: 4 })
const cache = new WeakMap<Client, Map<string, Promise<Message | undefined>>>()

function loadResult(client: Client, hit: SearchHit) {
  let messages = cache.get(client)
  if (!messages) {
    messages = new Map()
    cache.set(client, messages)
  }
  const key = `${hit.document.chat_jid}:${hit.document.id}:${hit.document.timestamp}`
  const existing = messages.get(key)
  if (existing) return existing
  const pending = queue
    .add(async () => {
      try {
        // Restore XML-only information (references, proper text, file details)
        // with one exact timestamp query, rather than fetch a conversation per row.
        const page = await fetchSearchHistoryPage({
          client,
          chatJid: hit.document.chat_jid,
          direction: "newer",
          timestamp: hit.document.timestamp,
          endTimestamp: hit.document.timestamp,
          pageSize: 1,
        })
        return [...page.payload.messages, ...page.payload.corrections].find(
          (message) => isSearchHistoryAnchor(message, hit.document),
        )
      } catch {
        return undefined
      }
    })
    .then((message) => message || undefined)
  messages.set(key, pending)
  const oldestKey = messages.keys().next().value
  if (messages.size > 200 && oldestKey) messages.delete(oldestKey)
  return pending
}

export default function useSearchResultMessage(hit: SearchHit) {
  const { client } = useContext(XmppContext)
  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin: "100px",
    fallbackInView: false,
  })
  const [message, setMessage] = useState<Message>()
  const rich = !!(
    hit.document.extractedMetadata ||
    hit.document.documentDriverContext ||
    hit.document.hasFile ||
    /shared-route\//i.test(hit.document.body || "")
  )
  useEffect(() => {
    if (!inView || !rich) return
    let current = true
    void loadResult(client, hit).then((message) => {
      if (current) setMessage(message)
    })
    return () => {
      current = false
    }
  }, [client, hit, inView, rich])
  return { ref, message }
}
